import { useEffect, useState } from "react";
import PublicLayout from "@/components/layout/PublicLayout";
import SectionEyebrow from "@/components/common/SectionEyebrow";
import StatusBadge from "@/components/common/StatusBadge";
import { publicApi } from "@/lib/api";
import { ArrowUpRight } from "lucide-react";

export default function FoundingMembers() {
  const [members, setMembers] = useState([]);
  useEffect(() => {
    publicApi.foundingMembers().then(setMembers).catch(() => {});
  }, []);

  return (
    <PublicLayout title="Founding Members & Governance">
      <section className="relative bsca-dark bsca-cosmic bsca-grain -mt-16 md:-mt-20 lg:-mt-[88px] pt-36 md:pt-44 pb-24 overflow-hidden">
        <div className="bsca-field-lines" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-10 lg:px-14 bsca-fade-up">
          <SectionEyebrow dark>Governance & Advisory Circle</SectionEyebrow>
          <h1 className="font-serif text-white text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] max-w-3xl">
            Founding Members
          </h1>
          <p className="mt-6 text-white/75 text-lg max-w-2xl">
            BSCA{"'"}s Advisory Circle is a globally distributed group of scientists, technologists, and scholars guiding the alliance{"'"}s research and ethics.
          </p>
        </div>
      </section>

      <section className="bg-[#F8F6F1] py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 bsca-stagger">
          {members.map((m) => (
            <article
              key={m.slug}
              data-testid={`member-card-${m.slug}`}
              className="bg-white border border-black/10 overflow-hidden group hover:border-[#D4AF37]/50 transition-colors"
            >
              <div className="aspect-[4/5] relative overflow-hidden bg-[#F8F6F1]">
                {m.image && (
                  <img src={m.image} alt={m.name} loading="lazy" className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                )}
                <div className="absolute top-3 left-3"><StatusBadge status={m.status} /></div>
              </div>
              <div className="p-6">
                <div className="bsca-eyebrow text-[#7A5A06] text-[10px]">{m.role_category}</div>
                <h3 className="mt-2 font-serif text-xl leading-tight">{m.name}</h3>
                <div className="mt-1 text-sm text-[#4B5563]">{m.title}</div>
                {m.credentials && (
                  <div className="mt-2 text-[11px] text-[#4B5563] italic leading-snug">{m.credentials}</div>
                )}
                <p className="mt-4 text-sm text-[#4B5563] leading-relaxed line-clamp-4">{m.short_bio}</p>
                {m.expertise_tags && m.expertise_tags.length > 0 && (
                  <div className="mt-4 flex flex-wrap gap-1.5">
                    {m.expertise_tags.slice(0, 4).map((t) => (
                      <span key={t} className="inline-flex items-center text-[10px] tracking-wide px-2 py-0.5 rounded-full bg-[#FBF3D5] text-[#7A5A06] border border-[#D4AF37]/30">{t}</span>
                    ))}
                  </div>
                )}
                <div className="mt-5 flex items-center justify-between text-xs">
                  <span className="text-[#9CA3AF]">{m.organization || m.institution}</span>
                  {m.profile_link ? (
                    <a href={m.profile_link} target="_blank" rel="noopener noreferrer" className="bsca-link inline-flex items-center">Profile <ArrowUpRight className="h-3 w-3 ml-1" /></a>
                  ) : (
                    <span className="text-[#9CA3AF] italic">Profile link pending</span>
                  )}
                </div>
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="bg-white py-20">
        <div className="max-w-3xl mx-auto px-6 md:px-10 text-center">
          <SectionEyebrow>Note from BSCA</SectionEyebrow>
          <p className="text-[#4B5563] leading-relaxed">
            Member portraits shown are interim CMS images. Final approved portraits (provided in the BSCA asset pack) will be uploaded in the next CMS batch. Foreword PDFs for each member are held in the BSCA editorial archive.
          </p>
        </div>
      </section>
    </PublicLayout>
  );
}
